import { inputIdFocusSelector } from '@common/redux/selectors/formInputs';
import { IInput } from '@common/types/element';
import { getValueBindingComponent } from '@common/utils/component';
import { debounce, replace } from 'lodash';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform, Text, TextInput, View } from 'react-native';
import { useSelector } from 'react-redux';
import connectorInput from '../ConnectorInput';
import createStyles from './style';

const CusInput: FC<IInput> = (attributes) => {
  const { changeInput, valueInput, autoFocus, id, inputError } = attributes;
  const inputRef = useRef<TextInput>(null);
  const inputIdFocus = useSelector(inputIdFocusSelector);
  const isFocusByAction = useMemo(
    () => inputIdFocus === id,
    [inputIdFocus, id]
  );

  const styles = createStyles(attributes);
  const keyBoardType = getKeyBoardType(
    attributes.inputType,
    attributes.isInteger
  );

  const isInt = attributes.isInteger;
  const isTel = attributes.inputType === 'tel';
  const maxLength =
    +getValueBindingComponent(attributes, 'maxLength') ||
    +attributes.maxLength?.parsedFormula ||
    undefined;

  const isPassword = attributes.inputType === 'password';
  const isEmail = attributes.id === 'email';
  const isName = attributes.id === 'fullName';

  const checkMultiline = () => {
    if ((isPassword && isEmail && isName) || attributes.multiline === false) {
      return false;
    } else {
      return attributes.multiline;
    }
  };

  // use local value + debounce to prevent multiple rerenders
  const [localValue, setLocalValue] = useState(() => sanitizeValue(valueInput));

  const handleChangeDebounced = useMemo(
    () =>
      debounce((value) => {
        changeInput(value);
      }, 300),
    [changeInput]
  );

  const handleChange = useCallback(
    (value) => {
      if (isInt) {
        if (/^\d+$/.test(value) || value === '') {
          const nextValue = convertNumberInt(value);
          setLocalValue(nextValue);
          handleChangeDebounced(nextValue);
        }
        return;
      }

      if (isTel) {
        if (/^[0-9+-]+$/.test(value) || value === '') {
          const nextValue = convertNumberToTel(value);
          setLocalValue(nextValue);
          handleChangeDebounced(nextValue);
        }
        return;
      }

      const floatRegex = /^[+-]?\d*(?:[.,]\d*)?$/;
      const isChangeValue =
        attributes.inputType !== 'number' ||
        (attributes.inputType === 'number' && floatRegex.test(value));
      if (isChangeValue) {
        setLocalValue(value);
        if (attributes.inputType === 'number') {
          handleChangeDebounced(value.replace(',', '.'));
        } else {
          handleChangeDebounced(value);
        }
      }
    },
    [attributes.inputType, handleChangeDebounced, isInt, isTel]
  );

  // forcus by action
  useEffect(() => {
    if (inputRef.current && isFocusByAction) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [inputRef.current, isFocusByAction]);

  // focus by default
  useEffect(() => {
    if (inputRef.current && autoFocus) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [inputRef.current]);

  // cancel debounce when component unmounts
  useEffect(() => {
    return () => {
      handleChangeDebounced.cancel();
    };
  }, [handleChangeDebounced]);

  // update local value when valueInput changes
  // debounce to prevent multiple rerenders
  useEffect(() => {
    const nextValue = sanitizeValue(valueInput);
    if (nextValue !== localValue) {
      const timeout = setTimeout(() => {
        setLocalValue(nextValue);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [valueInput]);

  return (
    <View
      style={[styles.container, styles.wrapper]}
      nativeID={`input-wrapper-${attributes.id}`}
    >
      <TextInput
        ref={inputRef}
        nativeID={`input-${attributes.id}`}
        style={[styles.text, inputError ? styles.errorWrapper : {}]}
        onChangeText={handleChange}
        placeholder={formatValue(attributes.placeholder)}
        placeholderTextColor={
          attributes.style?.placeholderColor || attributes.placeholderColor
        }
        keyboardType={keyBoardType}
        multiline={checkMultiline()}
        secureTextEntry={isPassword}
        value={localValue}
        maxLength={maxLength}
        textAlignVertical={
          checkMultiline() && Platform.OS === 'android' ? 'top' : 'auto'
        }
      />
      {inputError ? (
        <Text style={[styles.errorMessage]}>{inputError}</Text>
      ) : null}
    </View>
  );
};

const sanitizeValue = (value: any) => {
  return ['string', 'number'].includes(typeof value) ? value.toString() : '';
};

const convertNumberToTel = (number: string) => {
  const numberArr = number?.split('-').join('').split('') || [];
  let numberNew: string[] = [];

  numberArr.forEach((i: string, index: number) => {
    if (index < 3) return numberNew.push(i);
    if ((index - 3) % 4 === 0) return numberNew.push('-', i);
    return numberNew.push(i);
  });

  return numberNew.join('');
};

const convertNumberInt = (number: string) => {
  if (!number) return number;
  return number.match(/\d+/g)?.join('');
};

const getKeyBoardType = (inputType: string, isInteger?: boolean) => {
  if (isInteger) {
    if (Platform.OS === 'android') {
      return 'number-pad';
    }
    return 'number-pad';
  }

  switch (inputType) {
    case 'email':
      return 'email-address';

    case 'number':
      return 'decimal-pad';
    case 'tel':
      return 'phone-pad';

    default:
      return 'default';
  }
};

const formatValue = (str: any) =>
  replace(str, /<br ?\/?>/g, '\n')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/<\/?p[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/<\s*\/?\s*span\s*.*?>/g, '')
    .replace(/<[^>]*>/g, '\n')
    .replace(/&amp;/g, '&')
    .replace(/\n*$/g, '');

export default connectorInput(CusInput);
